import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { applyAccountToSession, saveOrUpdateAccount } from '../lib/session';
import { t } from '../lib/i18n';
import { api } from '../lib/trpc/client';
import { arrayBufferToBase64 } from '../lib/crypto';

export function LoginPage() {
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handlePasskeyLogin = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const options = await api.auth.passkeyLoginBegin();
      
      const challenge = Uint8Array.from(atob(options.challenge.replace(/-/g, '+').replace(/_/g, '/')), c => c.charCodeAt(0));
      
      const credential = await navigator.credentials.get({
        publicKey: {
          challenge,
          userVerification: 'required',
          timeout: 60000
        }
      }) as PublicKeyCredential;
      const response = credential.response as AuthenticatorAssertionResponse;
      
      const result = await api.auth.passkeyLoginComplete({
        credentialId: credential.id,
        clientDataJSON: arrayBufferToBase64(response.clientDataJSON),
        authenticatorData: arrayBufferToBase64(response.authenticatorData),
        signature: arrayBufferToBase64(response.signature)
      });
      
      const identitySkRaw = localStorage.getItem('identitySkRaw') ?? undefined;
      const account = saveOrUpdateAccount({
        userId: result.userId,
        username: result.scratchUsername,
        accessToken: result.accessToken,
        refreshToken: result.refreshToken,
        ...(identitySkRaw ? { identitySkRaw } : {})
      });
      applyAccountToSession(account);
      
      console.log('Login complete:', result.userId);
      if (identitySkRaw) {
        navigate('/circles');
      } else {
        navigate('/unlock');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('エラーが発生しました', 'An error occurred'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="flex min-h-screen items-center justify-center px-4 py-10">
        <Card className="w-full max-w-md border-border/80">
        <CardHeader>
          <CardTitle>{t('ログイン', 'Login')}</CardTitle>
          <CardDescription>{t('登録したPasskeyで安全にサインインします。', 'Sign in safely with your registered passkey.')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error ? (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}

          {loading ? (
            <div className="space-y-3">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          ) : (
            <Button onClick={handlePasskeyLogin} className="w-full">
              {t('Passkeyでログイン', 'Login with Passkey')}
            </Button>
          )}

          <p className="text-xs text-muted-foreground">{t('この端末に登録済みのPasskeyを選択してログインします。', 'Select a passkey already registered on this device to sign in.')}</p>

          <p className="text-center text-sm text-muted-foreground">
            {t('アカウントを持っていない場合は', "Don't have an account?")}{' '}
            <Link className="underline decoration-dotted underline-offset-4" to="/register">
              {t('新規登録', 'Register')}
            </Link>
          </p>
        </CardContent>
      </Card>
    </main>
  );
}
